import { useEffect, useState } from "react";
import axios from "axios";
import { Table, Card, Spinner } from "react-bootstrap";
import { GeoParroquia } from '../types/GeoParroquia';
import { GeoCanton } from '../types/GeoCanton';
import { GeoProvincia } from '../types/GeoProvincia';
import { Zona } from '../types/Zona';
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend
} from "chart.js";

const API_URL = process.env.REACT_APP_API_URL;

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend
);

function GeoDashboard() {
  const [provincias, setProvincias] = useState<GeoProvincia[]>([]);
  const [cantones, setCantones] = useState<GeoCanton[]>([]);
  const [parroquias, setParroquias] = useState<GeoParroquia[]>([]);
  const [zonas, setZonas] = useState<Zona[]>([]);

  const [selectedProv, setSelectedProv] = useState<GeoProvincia | null>(null);
  const [selectedCanton, setSelectedCanton] = useState<GeoCanton | null>(null);
  const [selectedParroquia, setSelectedParroquia] = useState<GeoParroquia | null>(null);

  const [loading, setLoading] = useState(false);

  const formatNumber = (n: number) =>
    new Intl.NumberFormat("es-EC").format(n);

  type RowData = {
    [key: string]: any;
  };

  type Level = "provincia" | "canton" | "parroquia" | "zona";

  // Cargar provincias al inicio
  useEffect(() => {
    loadProvincias();
  }, []);

  const loadProvincias = async () => {
    setLoading(true);
    const res = await axios.get<GeoProvincia[]>(`${API_URL}/geo/provincias`);
    setProvincias(res.data);
    setLoading(false);
  };

  const loadCantones = async (codprov: string) => {
    setLoading(true);
    const res = await axios.get<GeoCanton[]>(`${API_URL}/geo/provincias/${codprov}/cantones`);
    setCantones(res.data);
    setParroquias([]);
    setZonas([]);
    setLoading(false);
  };

  const loadParroquias = async (codcanton: string) => {
    setLoading(true);
    const res = await axios.get<GeoParroquia[]>(`${API_URL}/geo/cantones/${codcanton}/parroquias`);
    setParroquias(res.data);
    setZonas([]);
    setLoading(false);
  };

  const loadZonas = async (codparroquia: string) => {
    setLoading(true);
    const res = await axios.get<Zona[]>(`${API_URL}/geo/parroquias/${codparroquia}/zonas`);
    setZonas(res.data);
    setLoading(false);
  };

const totalGeneral = provincias.reduce(
  (acc, p) => ({
    contactos: acc.contactos + p.total_contactos,
    telefonos: acc.telefonos + p.total_telefonos,
    correos: acc.correos + p.total_correos
  }),
  { contactos: 0, telefonos: 0, correos: 0 }
);

const levelColors = {
  provincia: "#e8f1ff",
  canton: "#e8ffe8",
  parroquia: "#fff4e5",
  zona: "#f3e8ff"
};

const rowHoverStyle: React.CSSProperties = {
  cursor: "pointer",
  transition: "background-color 0.2s"
};

const numberColumnStyle: React.CSSProperties = {
  width: "140px",
  textAlign: "right",
  whiteSpace: "nowrap"
};

const provinciasChart = {
  labels: provincias.map((p) => p.provincia),
  datasets: [
    {
      label: "Contactos",
      data: provincias.map((p) => p.total_contactos),
      backgroundColor: "#4a90e2"
    }
  ]
};

const chartOptions = {
  responsive: true,
  plugins: {
    legend: { position: "top" as const },
  },
  scales: {
    x: { ticks: { autoSkip: false } },
    y: { beginAtZero: true }
  }
};

const renderTable = <T extends object>(
  title: string,
  data: T[],
  onSelect: (row: T) => void,
  keyField: keyof T,
  nameField: keyof T,
  level: Level
) => (     
  <Card className="mb-3">    
    <Card.Header style={{ backgroundColor: levelColors[level] }}>
      <strong>{title}</strong>
    </Card.Header>
    <div style={{ maxHeight: "232px", overflowY: "auto" }}>
      <Table striped bordered hover size="sm">
        <thead>
          <tr>
            <th>{String(nameField)}</th>
            <th>Total Contactos</th>
            <th>Total Teléfonos</th>
            <th>Total Correos</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr
            key={String(row[keyField])}
            style={rowHoverStyle}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f0f8ff")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
            onClick={() => onSelect(row)}
            >
                <td>{String(row[nameField])}</td>
                <td style={numberColumnStyle}>
                    {formatNumber((row as any).total_contactos)}
                </td>
                <td style={numberColumnStyle}>
                    {formatNumber((row as any).total_telefonos)}
                </td>
                <td style={numberColumnStyle}>
                    {formatNumber((row as any).total_correos)}
                </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  </Card>
);

  return (
    <div>
      <h2 className="mb-4">Análisis Geográfico</h2>

      {loading && (
        <div className="text-center mb-3">
          <Spinner animation="border" />
        </div>
      )}

    <Card className="mb-4">
        <Card.Header><strong>Totales Generales</strong></Card.Header>
        <Card.Body>
            <div className="d-flex gap-4">
            <div><strong>Contactos:</strong> {formatNumber(totalGeneral.contactos)}</div>
            <div><strong>Teléfonos:</strong> {formatNumber(totalGeneral.telefonos)}</div>
            <div><strong>Correos:</strong> {formatNumber(totalGeneral.correos)}</div>
            </div>
        </Card.Body>
    </Card>
      {/* Provincias */}
      {renderTable<GeoProvincia>(
        "Provincias",
        provincias,
        (prov) => {
          setSelectedProv(prov);
          setSelectedCanton(null);
          setSelectedParroquia(null);
          setCantones([]);
          setParroquias([]);
          setZonas([]);
          loadCantones(prov.codprov);
        },
        "codprov",
        "provincia",
        "provincia"
      )}

{/* 
<Card className="mb-4">
  <Card.Header><strong>Distribución por Provincias</strong></Card.Header>
  <Card.Body>
    <Bar data={provinciasChart} options={chartOptions} />
  </Card.Body>
</Card>
*/}
      {/* Cantones */}
      {selectedProv && cantones.length > 0 &&
        renderTable<GeoCanton>(
          `Cantones de ${selectedProv.provincia}`,
          cantones,
          (canton) => {
            setSelectedCanton(canton);
            setSelectedParroquia(null);
            setParroquias([]);
            setZonas([]);
            loadParroquias(canton.codcanton);
          },
          "codcanton",
          "canton",
          "canton"
        )
      }

      {/* Parroquias */}
      {selectedCanton && parroquias.length > 0 &&
        renderTable<GeoParroquia>(
          `Parroquias de ${selectedCanton.canton}`,
          parroquias,
          (parr) => {
            setSelectedParroquia(parr);
            setZonas([]);
            loadZonas(parr.codparroquia);
          },
          "codparroquia",
          "parroquia",
          "parroquia"
        )
      }

      {/* Zonas */}
      {selectedParroquia && zonas.length > 0 &&
        renderTable<Zona>(
          `Zonas de ${selectedParroquia.parroquia}`,
          zonas,
          () => {},
          "zona",
          "zona",
          "zona"
        )
      }
    </div>
  );
}

export default GeoDashboard;